import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  Camera,
  Check,
  ChevronDown,
  Database,
  FileSpreadsheet,
  Globe2,
  Linkedin,
  Mail,
  MessageCircle,
  MousePointerClick,
  Music2,
  PenLine,
  Search,
} from "lucide-react";
import clsx from "clsx";

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  keywords?: string;
}

interface SearchableSelectProps {
  value: string | number;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
  compact?: boolean;
  ariaLabel?: string;
}

function normalise(value: string) {
  return value
    .toLocaleLowerCase("ar")
    .normalize("NFKD")
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .trim();
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "اختر...",
  searchPlaceholder = "ابحث في القائمة...",
  emptyLabel = "لا توجد نتائج مطابقة",
  disabled = false,
  className,
  compact = false,
  ariaLabel,
}: SearchableSelectProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const selected = options.find((item) => item.value === String(value));
  const selectedIndex = options.findIndex(
    (item) => item.value === String(value),
  );
  const filtered = useMemo(() => {
    const needle = normalise(query);
    if (!needle) return options;
    return options.filter((item) =>
      normalise(`${item.label} ${item.value} ${item.keywords || ""}`).includes(
        needle,
      ),
    );
  }, [options, query]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(Math.max(0, selectedIndex));
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open, selectedIndex]);

  useEffect(() => setActiveIndex(0), [query]);

  const choose = (item: SelectOption) => {
    onChange(item.value);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(filtered.length - 1, index + 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(0, index - 1));
      return;
    }
    if (event.key === "Enter" && open && filtered[activeIndex]) {
      event.preventDefault();
      choose(filtered[activeIndex]);
    }
  };

  return (
    <div
      ref={rootRef}
      className={clsx(
        "searchable-select",
        compact && "is-compact",
        open && "is-open",
        className,
      )}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="searchable-select-trigger"
        onClick={() => !disabled && setOpen((current) => !current)}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || placeholder}
      >
        <span className={clsx("selected-value", !selected && "is-placeholder")}>
          {selected?.icon && <i aria-hidden="true">{selected.icon}</i>}
          <b dir="auto">{selected?.label || placeholder}</b>
        </span>
        <ChevronDown className="select-chevron" size={17} aria-hidden="true" />
      </button>
      {open && (
        <div className="searchable-select-menu">
          <label className="select-search">
            <Search size={16} aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              autoComplete="off"
            />
          </label>
          <div className="select-options" role="listbox">
            {filtered.length ? (
              filtered.map((item, index) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={item.value === String(value)}
                  className={clsx(
                    "select-option",
                    index === activeIndex && "is-active",
                    item.value === String(value) && "is-selected",
                  )}
                  key={`${item.value}-${index}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => choose(item)}
                >
                  <span>
                    {item.icon && <i aria-hidden="true">{item.icon}</i>}
                    <b dir="auto">{item.label}</b>
                  </span>
                  {item.value === String(value) && <Check size={16} />}
                </button>
              ))
            ) : (
              <div className="select-empty">{emptyLabel}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const sourceAliases: Record<string, string> = {
  google_ads: "google",
  gclid: "google",
  wa: "whatsapp",
  facebook: "meta",
  instagram: "meta",
  ig: "meta",
  linkedin_ads: "linkedin",
  landing_page: "landing",
  "landing page": "landing",
  website: "landing",
  organic: "landing",
  form: "landing",
};

function sourceKey(value?: string | null) {
  const key = (value || "other").toLocaleLowerCase("en").trim();
  return sourceAliases[key] || key;
}

export function SourceIcon({
  source,
  size = 17,
}: {
  source?: string | null;
  size?: number;
}) {
  const key = sourceKey(source);
  const props = { size, strokeWidth: 2.15 };
  let icon: ReactNode;
  if (key === "google") icon = <strong className="google-letter">G</strong>;
  else if (key === "linkedin") icon = <Linkedin {...props} />;
  else if (key === "whatsapp") icon = <MessageCircle {...props} />;
  else if (key === "meta") icon = <Camera {...props} />;
  else if (key === "snapchat") icon = <MessageCircle {...props} />;
  else if (key === "tiktok") icon = <Music2 {...props} />;
  else if (key === "email") icon = <Mail {...props} />;
  else if (key === "manual") icon = <PenLine {...props} />;
  else if (key === "csv") icon = <FileSpreadsheet {...props} />;
  else if (["apollo", "seed", "database"].includes(key))
    icon = <Database {...props} />;
  else if (key === "landing") icon = <MousePointerClick {...props} />;
  else icon = <Globe2 {...props} />;
  return (
    <span className={`source-icon source-${key}`} aria-hidden="true">
      {icon}
    </span>
  );
}

export function SourceBadge({
  source,
  label,
}: {
  source?: string | null;
  label?: string | null;
}) {
  const visibleLabel = label || source || "أخرى";
  return (
    <span className="source-badge" title={`المصدر: ${visibleLabel}`}>
      <SourceIcon source={source} />
      <b dir="auto">{visibleLabel}</b>
    </span>
  );
}
